import React from "react"
import CalendarTutorial from "../../components/calendar-tutorial"

const tutorial = {
  title: "Move Your Calendar Events with One Chat Request",
  description:
    "Shift calendar events earlier or later with a chat request. Review the selected events, confirm the move, and republish your updated schedule.",
  intro:
    "When a whole plan needs to start later, you can ask the calendar chat to shift its events together. Use a small practice calendar to learn the request, review, and confirmation steps before applying them to your own schedule.",
  tableTitle: "The practice schedule before the move",
  rows: [
    ["Monday", "9:00 am", "Reading", "30 min"],
    ["Monday", "10:00 am", "Maths", "45 min"],
    ["Monday", "11:00 am", "Library", "30 min"],
  ],
  prompt: "Move all schedules 1 hour later",
  promptInstructions:
    "First add the three example events to a separate practice calendar. Then send this request in Magic Generate. It applies to all events in the active calendar.",
  steps: [
    {
      title: "1. Start with a small practice calendar",
      paragraphs: [
        "Choose New blank calendar and name it “Schedule move practice” in Calendar Settings. Add the three Monday events above using a date you choose.",
        "Check the active calendar’s title before sending the request. “All schedules” selects every event in that calendar, including events outside the week currently visible.",
      ],
    },
    {
      title: "2. Ask for the change you want",
      paragraphs: [
        "Open Magic Generate and send “Move all schedules 1 hour later”. The chat identifies the events to move and asks you to confirm before applying the change.",
        "For a narrower request, you can specify a supported date range, such as “Move today’s schedules 30 minutes later”. Check the resulting event list to make sure it includes the sessions you intended.",
      ],
    },
    {
      title: "3. Review the proposed selection",
      paragraphs: [
        "Check the event names, dates, and number of selected schedules. For this example, there should be three events. A one-hour move changes Reading from 9am to 10am, Maths from 10am to 11am, and Library from 11am to noon.",
        "If the selection is wrong, send “cancel” before confirming. You can also narrow a pending selection with feedback such as “only Reading”; review the revised list before continuing.",
      ],
    },
    {
      title: "4. Confirm and inspect the calendar",
      paragraphs: [
        "Select Confirm move or send “yes” in the chat to apply the pending move. The selected events’ start and end times shift by the requested amount, preserving their durations.",
        "For the full example, Reading should now end at 10:30am, Maths at 11:45am, and Library at 12:30pm. Inspect the calendar for overlaps with any events that did not move.",
      ],
    },
    {
      title: "5. Publish the updated schedule if you share it",
      paragraphs: [
        "Once the calendar is correct, open Publish calendar and choose Republish for an existing published calendar. The same URL will show the new times when viewers reopen or refresh it.",
        "If families need to act on the change, tell them through your normal communication channel. The chat confirmation changes your calendar; it does not publish it or notify viewers automatically.",
      ],
    },
  ],
  cta: "Try a calendar schedule change",
  relatedUrl: "/use-cases/weekly-classroom-calendar/",
  relatedTitle: "Create a Weekly Classroom Calendar and Share It with Parents",
}

export default function TutorialPage() {
  return <CalendarTutorial {...tutorial} />
}
